import { create } from 'zustand';
import { apiClient } from '@/api/client';

interface DatabaseMeta {
  name: string;
  schemas: SchemaMeta[];
}

interface SchemaMeta {
  name: string;
  tables: TableMeta[];
  views: ViewMeta[];
  functions: FunctionMeta[];
}

interface TableMeta {
  name: string;
  schema: string;
  columns: ColumnMeta[];
  rowCountEstimate?: number;
}

interface ColumnMeta {
  name: string;
  type: string;
  nullable: boolean;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
}

interface ViewMeta { name: string; schema: string; }
interface FunctionMeta { name: string; schema: string; returnType: string; }

export interface CachedSchema {
  connectionId: string;
  database: string;
  metadata: { databases: DatabaseMeta[] };
  fetchedAt: string;
}

/**
 * Mirror of `/api/cache/schemas`. Loaded once on auth success and refreshed
 * on demand; search providers read from here synchronously. Keeping it
 * separate from TanStack Query lets us deliver truly cross-connection
 * results (including disconnected ones) without hijacking the per-connection
 * query caches the sidebar already manages.
 */
interface GlobalSchemaStore {
  schemas: CachedSchema[];
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useGlobalSchemaStore = create<GlobalSchemaStore>((set, get) => ({
  schemas: [],
  loaded: false,
  loading: false,

  async load() {
    if (get().loaded || get().loading) return;
    set({ loading: true });
    try {
      const schemas = await apiClient.get<CachedSchema[]>('/api/cache/schemas');
      set({ schemas: schemas ?? [], loaded: true, loading: false });
    } catch {
      // No cache yet or endpoint failed — start empty, don't block the app
      set({ loaded: true, loading: false });
    }
  },

  async refresh() {
    set({ loading: true });
    try {
      const schemas = await apiClient.get<CachedSchema[]>('/api/cache/schemas');
      set({ schemas: schemas ?? [], loaded: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },
}));

// Re-export the shapes so providers don't have to redeclare them.
export type { DatabaseMeta, SchemaMeta, TableMeta, ColumnMeta, ViewMeta, FunctionMeta };
